// How long a file is, at what rate, with how many channels — from its header.
//
// A stems folder can run to hundreds of megabytes, so WAV, AIFF and FLAC are
// read a few kilobytes at a time from the Blob and never decoded. MP3 has no
// trustworthy length in its header (VBR, encoder padding), so the caller
// decodes it; its sample rate does come from the first frame header, which is
// what the decode needs to keep the file's own rate.
//
// Each probe also says what the file WAS — format and bit depth — and so what
// it becomes: a converted file goes up as WAV at its source's depth (16-bit
// for 16-bit sources and MP3, 24-bit otherwise), never padded wider.
//
// Pure apart from the reader: every format is parsed from `read(offset, n)`.

import type { UploadSourceFormat } from "@shared/seed-history.js";

import type { AudioExtension } from "./config";
import type { WavDepth } from "./wav-out";

export interface AudioProbe {
  durationSec: number;
  sampleRate: number;
  channels: number;
  /** What the producer handed over. */
  format: UploadSourceFormat;
  /** The source's bit depth; null when it has none (MP3) or the header didn't say. */
  bitDepth: number | null;
  /** Bytes the upload will send for this file: as-is, or as WAV at `wavDepth` after conversion. */
  uploadBytes: number;
  /**
   * Read from a header the pipeline's own decoder understands (PCM or float
   * WAV, plain AIFF): the file can go up byte for byte. False ⇒ it was
   * decoded by the browser and goes up converted.
   */
  passthrough: boolean;
}

export type ByteReader = (offset: number, length: number) => Promise<Uint8Array>;

export function blobReader(blob: Blob): ByteReader {
  return async (offset, length) => new Uint8Array(await blob.slice(offset, offset + length).arrayBuffer());
}

export function formatOf(ext: AudioExtension): UploadSourceFormat {
  if (ext === "wave") return "wav";
  if (ext === "aif" || ext === "aifc") return "aiff";
  if (ext === "m4a") return "aac";
  return ext;
}

/**
 * The depth a converted file is written at: its own when that is 16-bit or
 * less (and for MP3, whose decoded output has no more than that to give),
 * 24-bit for anything deeper or unknown.
 */
export function wavDepthFor(format: UploadSourceFormat, bitDepth: number | null): WavDepth {
  if (format === "mp3" || format === "aac") return 16;
  return bitDepth !== null && bitDepth <= 16 ? 16 : 24;
}

/** The WAV a decoded file becomes: 44-byte header plus PCM. */
export function wavBytes(frames: number, channels: number, bits: WavDepth): number {
  return 44 + frames * channels * (bits / 8);
}

const id = (b: Uint8Array, o: number): string => String.fromCharCode(b[o]!, b[o + 1]!, b[o + 2]!, b[o + 3]!);

/** Null when the header isn't one this format's parser understands. MP3 and AAC always need `decodedProbe`. */
export async function probeHeader(ext: AudioExtension, size: number, read: ByteReader): Promise<AudioProbe | null> {
  switch (ext) {
    case "wav":
    case "wave":
      return probeWav(size, read);
    case "aif":
    case "aiff":
    case "aifc":
      return probeAiff(size, read);
    case "flac":
      return probeFlac(read);
    case "mp3":
    case "m4a":
    case "aac":
      return null;
  }
}

/** A decoded buffer's probe — what MP3 (or a header that wouldn't parse) gets. */
export function decodedProbe(
  buffer: { length: number; sampleRate: number; numberOfChannels: number },
  format: UploadSourceFormat,
  bitDepth: number | null = null,
): AudioProbe {
  return {
    durationSec: buffer.length / buffer.sampleRate,
    sampleRate: buffer.sampleRate,
    channels: buffer.numberOfChannels,
    format,
    bitDepth,
    uploadBytes: wavBytes(buffer.length, buffer.numberOfChannels, wavDepthFor(format, bitDepth)),
    passthrough: false,
  };
}

async function probeWav(size: number, read: ByteReader): Promise<AudioProbe | null> {
  const head = await read(0, 12);
  if (head.length < 12 || id(head, 0) !== "RIFF" || id(head, 8) !== "WAVE") return null;
  let rate = 0;
  let channels = 0;
  let blockAlign = 0;
  let bits = 0;
  // Walk the chunks: LIST, bext, JUNK and friends can sit before `data`.
  for (let off = 12; off + 8 <= size; ) {
    const h = await read(off, 8 + 16);
    if (h.length < 8) return null;
    const v = new DataView(h.buffer, h.byteOffset, h.byteLength);
    const chunk = id(h, 0);
    const chunkSize = v.getUint32(4, true);
    if (chunk === "fmt " && h.length >= 24) {
      // PCM, IEEE float, or EXTENSIBLE: what shared/audio/master-mix.ts reads.
      // ADPCM, µ-law and friends are left to the browser to decode.
      if (![1, 3, 0xfffe].includes(v.getUint16(8, true))) return null;
      channels = v.getUint16(10, true);
      rate = v.getUint32(12, true);
      bits = v.getUint16(22, true);
      blockAlign = v.getUint16(20, true) || channels * (bits / 8);
    } else if (chunk === "data") {
      if (!rate || !blockAlign || !channels) return null;
      // A streaming writer may leave the size unset; what's there is the data.
      const bytes = chunkSize === 0xffffffff || off + 8 + chunkSize > size ? size - off - 8 : chunkSize;
      const frames = Math.floor(bytes / blockAlign);
      return {
        durationSec: frames / rate, sampleRate: rate, channels,
        format: "wav", bitDepth: bits || null, uploadBytes: size, passthrough: true,
      };
    }
    off += 8 + chunkSize + (chunkSize & 1);
  }
  return null;
}

async function probeAiff(size: number, read: ByteReader): Promise<AudioProbe | null> {
  const head = await read(0, 12);
  if (head.length < 12 || id(head, 0) !== "FORM" || (id(head, 8) !== "AIFF" && id(head, 8) !== "AIFC")) return null;
  const aifc = id(head, 8) === "AIFC";
  for (let off = 12; off + 8 <= size; ) {
    const h = await read(off, 8 + 22);
    if (h.length < 8) return null;
    const v = new DataView(h.buffer, h.byteOffset, h.byteLength);
    const chunkSize = v.getUint32(4);
    if (id(h, 0) === "COMM" && h.length >= 26) {
      const channels = v.getUint16(8);
      const frames = v.getUint32(10);
      const bits = v.getUint16(14);
      const rate = extended80(v, 16);
      if (!(rate > 0) || !channels) return null;
      // AIFC is only plain when uncompressed: NONE, or sowt (little-endian PCM).
      if (aifc && (h.length < 30 || !["NONE", "sowt"].includes(id(h, 26)))) return null;
      return {
        durationSec: frames / rate, sampleRate: rate, channels,
        format: "aiff", bitDepth: bits || null, uploadBytes: size, passthrough: true,
      };
    }
    off += 8 + chunkSize + (chunkSize & 1);
  }
  return null;
}

/** 80-bit IEEE-754 extended, big-endian (AIFF's sample rate). */
function extended80(v: DataView, off: number): number {
  const exponent = v.getUint16(off) & 0x7fff;
  const mantissa = v.getUint32(off + 2) * 2 ** 32 + v.getUint32(off + 6);
  if (exponent === 0 && mantissa === 0) return 0;
  return mantissa * 2 ** (exponent - 16383 - 63);
}

/** FLAC: `fLaC`, then STREAMINFO is always the first metadata block. */
async function probeFlac(read: ByteReader): Promise<AudioProbe | null> {
  let h = await read(0, 4 + 4 + 34);
  let base = 0;
  // An ID3v2 tag in front of a FLAC stream happens; skip it.
  if (h.length >= 10 && h[0] === 0x49 && h[1] === 0x44 && h[2] === 0x33) {
    base = 10 + syncsafe(h, 6);
    h = await read(base, 4 + 4 + 34);
  }
  if (h.length < 42 || id(h, 0) !== "fLaC" || (h[4]! & 0x7f) !== 0) return null;
  const s = 8; // STREAMINFO body
  // Bits 80–99: sample rate (20), 100–102: channels − 1 (3), 108–143: total samples (36).
  const rate = (h[s + 10]! << 12) | (h[s + 11]! << 4) | (h[s + 12]! >> 4);
  const channels = ((h[s + 12]! >> 1) & 0x07) + 1;
  const bits = (((h[s + 12]! & 0x01) << 4) | (h[s + 13]! >> 4)) + 1;
  const frames = (h[s + 13]! & 0x0f) * 2 ** 32 + ((h[s + 14]! << 24) >>> 0) + (h[s + 15]! << 16) + (h[s + 16]! << 8) + h[s + 17]!;
  if (!rate || !frames) return null; // 0 total samples means "unknown": decode instead
  return {
    durationSec: frames / rate, sampleRate: rate, channels,
    format: "flac", bitDepth: bits, uploadBytes: wavBytes(frames, channels, wavDepthFor("flac", bits)), passthrough: false,
  };
}

function syncsafe(b: Uint8Array, o: number): number {
  return (b[o]! << 21) | (b[o + 1]! << 14) | (b[o + 2]! << 7) | b[o + 3]!;
}

const MP3_RATES: Record<number, readonly number[]> = {
  3: [44100, 48000, 32000], // MPEG-1
  2: [22050, 24000, 16000], // MPEG-2
  0: [11025, 12000, 8000], // MPEG-2.5
};

/** The sample rate in an MP3's first frame header (after any ID3v2 tag), or null. */
export async function mp3SampleRate(read: ByteReader): Promise<number | null> {
  let base = 0;
  const tag = await read(0, 10);
  if (tag.length >= 10 && tag[0] === 0x49 && tag[1] === 0x44 && tag[2] === 0x33) base = 10 + syncsafe(tag, 6);
  const h = await read(base, 4096);
  for (let i = 0; i + 3 < h.length; i++) {
    if (h[i] !== 0xff || (h[i + 1]! & 0xe0) !== 0xe0) continue;
    const version = (h[i + 1]! >> 3) & 0x03;
    const layer = (h[i + 1]! >> 1) & 0x03;
    const rateIndex = (h[i + 2]! >> 2) & 0x03;
    const rates = MP3_RATES[version];
    if (!rates || layer === 0 || rateIndex === 3) continue;
    return rates[rateIndex]!;
  }
  return null;
}
